<template>
  <section class="email-editor-wrapper">
    <am2-select-events-modal
      v-if="location==='message-center'"
      :opened="displaySelectEventsModal"
      :default-event-oid="defaultEventOidForSelectEventsModal"
      @confirm="handleSelectEventConfirm"
      @cancel="handleSelectEventCancel"
    />
    <am2-select-dynamic-tags-modal
      v-if="location==='message-center'"
      :is-show="showSelectDynamicTagsModal"
      :dynamic-tags="dynamicTagsForBeefree"
      @select="handleDynamicTagsSelect"
      @cancel="handleDynamicTagsCancel"
    />
    <am2-loading-section
      v-if="!displayBeefreeEditor"
      class="loading-section"
    />
    <section
      ref="editor"
      class="editor"
      :style="{
        padding: $arMediaQuery.pageContent.maxWidth('sm') ? '30px 18px' : '47px 59px',
      }"
    >
      <div
        class="beefree-editor"
        :id="`beefree-editor-${beefreeEditorHash}`"
      ></div>
    </section>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { generateHash } from '@/utils/helpers'

import BeefreeSDK from '@beefree.io/sdk'

// BeeFree's Content Services renderer requires dash-case CSS property names inside
// style/mobileStyle maps (e.g. "background-color", not "backgroundColor"). The SDK
// can also reset grid-columns to 12 and strand the real column width in a gridColumns
// key. This function corrects both corruptions so saved designs stay valid.
//
// Collision rule (camelCase wins): when both "background-color" and "backgroundColor"
// are present, the camelCase value is the user's actual intent — the dash-case entry
// is stale. We achieve this by processing dash-case keys first, then camelCase keys
// so they overwrite on collision.
function camelToDash(str) {
  return str.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`);
}

function normalizeBeefreeCssKeys(obj) {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map(normalizeBeefreeCssKeys);
  const result = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if ((key === 'style' || key === 'mobileStyle') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // CSS style map: sort so dash-case keys are processed before camelCase keys,
      // ensuring the camelCase value wins when both forms exist for the same property.
      const cssKeys = Object.keys(value);
      const dashFirst = [
        ...cssKeys.filter(k => !/[A-Z]/.test(k)),
        ...cssKeys.filter(k => /[A-Z]/.test(k)),
      ];
      const normalized = {};
      for (const cssKey of dashFirst) {
        normalized[camelToDash(cssKey)] = value[cssKey];
      }
      result[key] = normalized;
    } else {
      result[key] = normalizeBeefreeCssKeys(value);
    }
  }
  // Reconcile grid-columns / gridColumns: SDK resets grid-columns to 12 and stores
  // the real column width in gridColumns. Promote the camelCase value and drop the key.
  if ('gridColumns' in result) {
    result['grid-columns'] = result['gridColumns'];
    delete result['gridColumns'];
  }
  return result;
}

const EMPTY_TEMPLATE = {
  "page": {
    "title": "",
    "description": "",
    "template": {
      "name": "template-base",
      "type": "basic",
      "version": "0.0.1"
    },
    "body": {
      "type": "mailup-bee-page-proprerties",
      "container": {
        "style": {
          "background-color": "#FFFFFF"
        }
      },
      "content": {
        "style": {
          "font-family": "Arial, 'Helvetica Neue', Helvetica, sans-serif",
          "color": "#000000"
        },
        "computedStyle": {
          "linkColor": "#0068A5",
          "messageBackgroundColor": "transparent",
          "messageWidth": "500px"
        }
      }
    },
    "rows": [
      {
        "type": "one-column-empty",
        "container": {
          "style": {
            "background-color": "transparent"
          }
        },
        "content": {
          "style": {
            "background-color": "transparent",
            "color": "#000000",
            "width": "500px"
          }
        },
        "columns": [
          {
            "grid-columns": 12,
            "modules": [
              {
                "type": "mailup-bee-newsletter-modules-empty",
                "descriptor": {}
              }
            ],
            "style": {
              "background-color": "transparent",
              "padding-top": "5px",
              "padding-right": "0px",
              "padding-bottom": "5px",
              "padding-left": "0px",
              "border-top": "0px dotted transparent",
              "border-right": "0px dotted transparent",
              "border-bottom": "0px dotted transparent",
              "border-left": "0px dotted transparent"
            }
          }
        ]
      }
    ]
  }
}

export default {
  name: 'BeefreeEmailEditor',

  props: {
    dynamicTags: {
      type: Array,
      default: null,
    },
    location: {
      type: String,
      default: 'message-center',
      validator: (val) => ['message-center', 'automation'].indexOf(val) > -1,
    }
  },

  data() {
    const outputDesign = (jsonFile) => {
      try {
        const design = typeof jsonFile === 'string' ? JSON.parse(jsonFile) : jsonFile;
        this.$emit('outputDesign', JSON.stringify(normalizeBeefreeCssKeys(design)));
      } catch (_) {
        this.$emit('outputDesign', jsonFile);
      }
    };

    return {
      design: null,
      displaySelectEventsModal: false,
      defaultEventOidForSelectEventsModal: null,

      // Beefree
      displayBeefreeEditor: false,
      beefreeSelectEventCallback: null,
      beefreeSelectEventCancelCallback: null,
      beefreeEditorHash: generateHash(),
      beePlugin: null,
      currentToken: null,
      beeConfig: {
        container: `beefree-editor`,
        language: 'en-US',
        trackChanges: true,
        loadingSpinnerTheme: 'light',
        contentDialog: {
          addOn: {
            label: 'Insert Event',
            handler: (resolve, reject, args) => {
              this.handleSelectEventClick(resolve, reject)
            }
          },
          mergeTags: {
            label: 'Dynamic tags',
            handler: (resolve, reject) => {
              this.handleSelectDynamicTagsModal(resolve, reject)
            }
          },
        },
        onChange: function (jsonFile, response) {
          outputDesign(jsonFile)
        },
        onComment: function (change, jsonFile) {
          outputDesign(jsonFile)
        },
        onTogglePreview: () => {
          this.updateShowPreview()
        },
        onWarning: function (warningMessage) {
          console.warn('OID', this.promoterOid, 'BEEFREE EDITOR WARN:', warningMessage);
        },
        onError: function (errorMessage) {
          console.error('OID', this.promoterOid, 'BEEFREE EDITOR ERR: ', errorMessage);
          // Handle V2 authentication errors with proper error object parsing
          if (errorMessage && typeof errorMessage === 'object') {
            if (errorMessage.code === 5101) {
              // 5101: "Expired token cannot be refreshed" - use updateToken() on existing instance
              this.handleTokenReauth(5101);
            } else if (errorMessage.code === 5102) {
              // 5102: "Expired token must be refreshed" - create new instance with error.template
              this.handleTokenReauth(5102, errorMessage.template);
            }
          } else if (errorMessage && errorMessage.includes('5101')) {
            // Fallback for string-based error messages (legacy compatibility)
            this.handleTokenReauth(5101);
          } else if (errorMessage && errorMessage.includes('5102')) {
            // Fallback for string-based error messages (legacy compatibility)
            this.handleTokenReauth(5102);
          }
        },
        contentDefaults: {},
      },
      showPreviewDynamicTags: false,
      showStructure: false,
      showPreview: false,
      showSelectDynamicTagsModal: false,
      selectDynamicTagsModalCallback: null,
      selectDynamicTagsModalCancelCallback: null,
    };
  },

  computed: {
    ...mapState({
      promoterOid: state => state.auth.account.promoterOid,
      accountOid: state => state.auth.account.oid,
      firstName: state => state.auth.account.firstName,
      lastName: state => state.auth.account.lastName,
    }),
    ...mapGetters({
      getEventImageUrl: 'event/getEventImageUrl',
    }),
    dynamicTagsForBeefree() {
      if (!this.dynamicTags) {
        return [];
      }

      // Tag samplers show up during Preview Dynamic Tags
      return this.dynamicTags.map(function (tag) {
        let tag_sampler = tag
        if (tag === 'email_address') {
          tag_sampler = "sampler@arep.co"
        } else if (tag === "first_name") {
          tag_sampler = "Mary Sue"
        }

        return {
          name: tag,
          value: `{{${tag}}}`,
          previewValue: tag_sampler,
        }
      });
    },
    beefreeEditorIsReadyForEditing() {
      return !!this.beePlugin;
    },
  },

  mounted() {
    this.initEmailEditor();
  },

  methods: {
    async getToken() {
      try {
        const token = await this.$api.beefree.initialiseBeefreePlugin(this.promoterOid)
        this.currentToken = token; // Store token for refresh purposes
        
        // Transform backend token to match BeeFree SDK IToken interface requirements
        // Backend returns: { accessToken: "...", v2: true } (camelCase)
        // SDK expects: { access_token: "...", v2: true, status: "valid", shared: false, coediting_session_id: null }
        const sdkToken = {
          access_token: token.accessToken || token.access_token,
          v2: token.v2 || true,
          status: "valid",
          shared: false,
          coediting_session_id: null
        };
        
        // Initialize BeefreeSDK instance with complete token object
        this.beePlugin = new BeefreeSDK(sdkToken);
        
        // Start the editor with configuration and template
        await this.beePlugin.start({
          ...this.beeConfig,
          container: `beefree-editor-${this.beefreeEditorHash}`,
          language: 'en-US',
          username: `${this.firstName} ${this.lastName}`,
          userHandle: this.accountOid,
        }, this.design || EMPTY_TEMPLATE);
        
        this.displayBeefreeEditor = true;
      } catch(error) {
        console.error("Failed to fetch Beefree Auth", error)
      }
    },
    async handleTokenReauth(errorCode = null, errorTemplate = null) {
      try {
        console.log(`Re-authenticating BeeFree token due to authentication error ${errorCode || 'unknown'}...`);
        
        // Get new token from backend (V2 approach: re-authenticate rather than refresh)
        const newToken = await this.$api.beefree.initialiseBeefreePlugin(this.promoterOid);
        this.currentToken = newToken;
        
        // Transform token to SDK format
        const newSdkToken = {
          access_token: newToken.accessToken || newToken.access_token,
          v2: newToken.v2 || true,
          status: "valid",
          shared: false,
          coediting_session_id: null
        };
        
        // Extract common plugin recreation logic to reduce duplication
        const recreatePlugin = (template = this.design || EMPTY_TEMPLATE) => {
          this.beePlugin = new BeefreeSDK(newSdkToken);
          this.beePlugin.start({
            ...this.beeConfig,
            container: `beefree-editor-${this.beefreeEditorHash}`,
            language: 'en-US',
            username: `${this.firstName} ${this.lastName}`,
            userHandle: this.accountOid,
          }, template);
        };
        
        if (errorCode === 5101) {
          // 5101: "Expired token cannot be refreshed" - use updateToken() on existing instance
          if (this.beePlugin && this.beePlugin.updateToken) {
            this.beePlugin.updateToken(newSdkToken);
          } else {
            // If updateToken not available, fallback to recreate
            recreatePlugin();
          }
        } else if (errorCode === 5102) {
          // 5102: "Expired token must be refreshed" - create new instance with error.template
          recreatePlugin(errorTemplate || this.design || EMPTY_TEMPLATE);
        } else {
          // Legacy/unknown error handling - use updateToken() if available, otherwise reinitialize
          if (this.beePlugin && this.beePlugin.updateToken) {
            this.beePlugin.updateToken(newSdkToken);
          } else {
            recreatePlugin();
          }
        }
      } catch(error) {
        console.error("Failed to re-authenticate Beefree token", error);
        this.$arNotification.push({
          type: 'error',
          message: 'BeeFree authentication expired. Please reload the page.',
        });
      }
    },
    setDesign(template_data) {
      // Loading the template into the editor
      const design = (!template_data) ? EMPTY_TEMPLATE : template_data;
      const parsed = typeof design === "string" ? JSON.parse(design) : design;
      this.design = normalizeBeefreeCssKeys(parsed);
      this.$emit("outputDesign", JSON.stringify(this.design))
      this.loadBeefreeDesign(this.design);
    },
    async loadBeefreeDesign(design) {
      if (!this.beefreeEditorIsReadyForEditing) {
        return;
      }

      this.beePlugin.load(design)
    },
    async initEmailEditor() {
      this.getToken()
      this.loadBeefreeDesign(this.design);
    },
    setEventInBeefree(event) {
      const tinyEvent = {
        oid: event.oid,
        name: event.name,
        description: event.description,
        location: event.location,
        date: this.$arUtils.event.generateDateForEmail(event),
        imageUrl: this.getEventImageUrl(event),
        purchaseTicketUrl: this.$arUtils.event.getEventPurchaseTicketUrl(event) || 'https://example.com',
      };

      if (!tinyEvent.imageUrl) {
        this.$arNotification.push({
          type: 'error',
          message: 'Event does not have an image',
        });
      }

      if (!tinyEvent.purchaseTicketUrl || tinyEvent.purchaseTicketUrl === 'https://example.com') {
        this.$arNotification.push({
          type: 'error',
          message: 'Add URL to the button "Purchase"',
        });
      }

      const rowAddonFromBeefree = {
        "type": "rowAddon",
        "value": {
          "name": "First item",
          "justify-content": "center",
          "align-items": "center",
          "columns": [{
            "weight": 4,
            "modules": [{
              "type": "image",
              "src": tinyEvent.imageUrl,
            }],
          }, {
            "weight": 5,
            "modules": [{
              "type": "paragraph",
              "text": `<span style="font-size: 16px;">${tinyEvent.name}</span><br />${tinyEvent.location}<br />${tinyEvent.date}`,
              "size": 14,
            }]
          }, {
            "weight": 3,
            "modules": [{
              "type": "button",
              "text": "Purchase",
              "href": tinyEvent.purchaseTicketUrl,
              "background-color": "#945bf1",
            }]
          }]
        },
      }

      this.beefreeSelectEventCallback(rowAddonFromBeefree)
    },
    handleSelectEventClick(resolve = null, reject = null) {
      if (this.location === 'automation') {
        this.$emit('selectEventClick')
      } else {
        this.displaySelectEventsModal = true;
      }
      this.defaultEventOidForSelectEventsModal = null;
      this.beefreeSelectEventCallback = resolve;
      this.beefreeSelectEventCancelCallback = reject;
    },
    handleSelectEventConfirm(event) {
      this.displaySelectEventsModal = false;
      this.setEventInBeefree(event);
    },
    handleSelectEventCancel() {
      this.displaySelectEventsModal = false;
      this.beefreeSelectEventCancelCallback("cancelled")
    },
    handleSelectDynamicTagsModal(resolve = null, reject = null) {
      if (this.location === 'automation') {
        this.$emit('selectDynamicTag')
      } else {
        this.showSelectDynamicTagsModal = true;
      }
      this.selectDynamicTagsModalCallback = resolve;
      this.selectDynamicTagsModalCancelCallback = reject;
    },
    handleDynamicTagsSelect(dynamicTag) {
      this.showSelectDynamicTagsModal = false;
      this.selectDynamicTagsModalCallback(dynamicTag)
    },
    handleDynamicTagsCancel() {
      this.showSelectDynamicTagsModal = false;
      this.selectDynamicTagsModalCancelCallback()
    },
    updateShowPreview() {
      this.showPreview = !this.showPreview
      this.$emit("setBeefreeShowPreview", this.showPreview)
    },
    preview() {
      if (!this.beePlugin || !this.beePlugin.instance) {
        return null;
      }
      // Beefree inside its editor already has a preview function, unlike Unlayer that we have to build an HTML to be able to preview.
      this.beePlugin.preview()
    },
    toggleStructureOutlines() {
      if (!this.beePlugin || !this.beePlugin.instance) {
        return null;
      }
      // Structure outlines are grids around the columns of Beefree
      this.beePlugin.toggleStructure()
      this.showStructure = !this.showStructure;
      this.$emit("setBeefreeShowStructure", this.showStructure)
    },
    previewDynamicTags() {
      if (!this.beePlugin || !this.beePlugin.instance) {
        return null;
      }
      this.beePlugin.toggleMergeTagsPreview()
      this.showPreviewDynamicTags = !this.showPreviewDynamicTags;
      this.$emit("setBeefreeShowPreviewDynamicTags", this.showPreviewDynamicTags)
    },
  },
};
</script>
<style lang="scss" scoped>
.email-editor-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  .editor {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: $zIndexRegular;

    .beefree-editor {
      position: absolute;
      top: 0px;
      bottom: 0px;
      left: 0px;
      right: 0px;
    }
  }

  .loading-section {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: white;
    z-index: $zIndexHigh;
  }
}


</style>
